/**
 * Distant rolling-hills "skirt" surrounding the playable square. Pure
 * decoration — ants never go here. Sits a touch below the play terrain
 * so the central area is visually occluded by the real ground; rises
 * outside the world radius into low-poly hills that fade into the sky
 * via the scene's exponential fog.
 *
 * One big plane, fbm-displaced, with a smooth radial mask that holds it
 * flat (and below) inside the playable square so it never pokes through
 * the real terrain at the boundary.
 */

import * as THREE from 'three';
import { createNoise2D } from 'simplex-noise';

interface SkirtOptions {
  /** Half-extent of the playable square (matches Terrain.size / 2). */
  worldRadius: number;
  /** How far past the playable edge the skirt extends. */
  outerExtent?: number;
  /** Vertex grid resolution. 96 keeps it cheap; the silhouette is broken
   *  up by fbm so a denser grid isn't necessary. */
  segments?: number;
  /** Hex base colour — picked to match the terrain's distant tone. */
  color?: number;
  /** Seed so the hills shape is stable across reloads. */
  seed?: number;
}

export class SkirtHills {
  readonly mesh: THREE.Mesh;

  constructor(opts: SkirtOptions) {
    const worldRadius = opts.worldRadius;
    const outerExtent = opts.outerExtent ?? worldRadius * 6;
    const segments = opts.segments ?? 96;
    const color = opts.color ?? 0x6f7e58;
    const seed = opts.seed ?? 1337;

    // Side length covers from -outerExtent to +outerExtent.
    const side = outerExtent * 2;
    const geo = new THREE.PlaneGeometry(side, side, segments, segments);
    geo.rotateX(-Math.PI / 2);

    // Seeded simplex via xorshift PRNG.
    let s = seed >>> 0;
    const rand = () => {
      s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
      return ((s >>> 0) / 0xffffffff);
    };
    const noise2D = createNoise2D(rand);

    // Multi-octave fbm — large dunes that read at distance.
    const fbm = (x: number, z: number): number => {
      let amp = 1, freq = 0.012, sum = 0;
      for (let o = 0; o < 4; o++) {
        sum += amp * noise2D(x * freq, z * freq);
        amp *= 0.5;
        freq *= 2.1;
      }
      return sum;
    };

    const pos = geo.attributes.position as THREE.BufferAttribute;
    // Inner radius — hills start rising past this. Add a small overlap
    // (~+5%) past the playable square's diagonal so the play terrain
    // visually covers the seam.
    const playDiag = worldRadius * Math.SQRT2;
    const innerR = playDiag * 1.05;
    const blendR = playDiag * 0.5;        // distance over which we ramp up
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const r = Math.hypot(x, z);
      // 0 inside the play area, 1 once we're well past the edge.
      const t = Math.min(1, Math.max(0, (r - innerR) / blendR));
      // Cosine-ramp for a soft transition without a visible kink.
      const mask = 0.5 - 0.5 * Math.cos(Math.PI * t);
      const baseSink = -0.6;              // how far below play-terrain
      const peakLift = 6.0;                // tallest hills (metres)
      const noiseH = fbm(x, z) * peakLift;
      pos.setY(i, baseSink + mask * (noiseH + peakLift * 0.5));
    }
    geo.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      color,
      roughness: 1.0,
      metalness: 0,
      flatShading: true,                  // low-poly facets read at distance
    });

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.receiveShadow = true;
    this.mesh.castShadow = false;
    this.mesh.name = 'skirt-hills';
    // Render before everything else so far hills sit visually behind
    // the play terrain. Fog density (exponential in scene) takes care
    // of the rest.
    this.mesh.renderOrder = -1;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
